"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ComponentProps } from "react";

import { Icon } from "@/components/typography/Icon";
import type { IconName } from "@/components/typography/Icon";
import { cn } from "@/lib/utils";

type TagProps = {
  label: string;
  href?: string;
  icon?: IconName;
  active?: boolean;
  asButton?: boolean;
} & Omit<ComponentProps<typeof Link>, "href">;

const Tag = ({
  label,
  href,
  icon,
  active = false,
  asButton = false,
  ...rest
}: TagProps) => {
  const router = useRouter();
  const baseStyles =
    "text-copy-sm inline-flex items-center gap-1 px-1.5 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap";
  const inactiveStyles =
    "border-black text-black-100 hover:bg-black hover:text-white";
  const activeStyles = "bg-black text-white border-black";

  if (href && asButton) {
    return (
      <button
        type="button"
        onClick={() => router.push(href)}
        className={cn(
          baseStyles,
          active ? activeStyles : inactiveStyles,
          rest.className
        )}
      >
        {icon && <Icon name={icon} />}
        {label}
      </button>
    );
  }

  if (href) {
    return (
      <Link
        href={href}
        className={cn(
          baseStyles,
          active ? activeStyles : inactiveStyles,
          rest.className
        )}
      >
        {icon && <Icon name={icon} />}
        {label}
      </Link>
    );
  }

  return (
    <span
      className={cn(
        baseStyles,
        active ? activeStyles : inactiveStyles,
        rest.className
      )}
    >
      {icon && <Icon name={icon} />}
      {label}
    </span>
  );
};

export { Tag };
